import { dayIn } from "../../shared/orgdate.js";
// Small helpers shared by every screen.

export function esc(v: unknown): string {
  return String(v ?? "").replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[m]!);
}

export class ApiError extends Error {
  /** Seconds to wait before trying again (a 429's Retry-After). */
  retryAfter?: number;
  constructor(public status: number, public code: string, message: string, public field?: string, public details?: any) {
    super(message);
  }
}

/** Called with the "signed in as" answer of every reply; returns true if the page must stop. */
let identityGuard: ((who: string) => boolean) | null = null;
export function setIdentityGuard(fn: (who: string) => boolean): void {
  identityGuard = fn;
}
/** Told which organisation this browser is working in (another tab may have switched): true stops the request. */
let orgGuard: ((org: string) => boolean) | null = null;
export function setOrgGuard(fn: (org: string) => boolean): void {
  orgGuard = fn;
}

/** Shown when a settings change needs the password or two-factor code again; resolves true once unlocked. */
let unlockPrompt: ((message: string) => Promise<boolean>) | null = null;
export function setUnlockPrompt(fn: (message: string) => Promise<boolean>): void {
  unlockPrompt = fn;
}

/** The longest the screens wait before trying a busy request again (they never sit waiting longer than this). */
export const BUSY_WAIT_MAX_MS = 8000;
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function api<T = any>(method: string, url: string, body?: unknown): Promise<T> {
  try {
    return await request<T>(method, url, body);
  } catch (err) {
    // Settings are locked: ask for the password / two-factor code, then send the same change again.
    if (err instanceof ApiError && err.code === "SETTINGS_LOCKED" && unlockPrompt && (await unlockPrompt(err.message))) return request<T>(method, url, body);
    // Falcon is busy for a moment (its speed limit): say so, wait as asked (a few seconds at most) and try once more.
    // Sign-in, password and code requests are never tried again by themselves.
    if (err instanceof ApiError && err.status === 429 && err.code === "RATE_LIMITED" && !/^\/api\/(auth|account)\//.test(url)) {
      toast("Falcon is busy, trying again…");
      await wait(Math.min(Math.max(Number(err.details?.retryAfter ?? err.retryAfter) || 2, 1) * 1000, BUSY_WAIT_MAX_MS));
      try {
        return await request<T>(method, url, body);
      } catch (again) {
        if (again instanceof ApiError && again.status === 429) throw new ApiError(429, "RATE_LIMITED", "Falcon is busy. Please wait a few seconds and try again.");
        throw again;
      }
    }
    throw err;
  }
}

async function request<T = any>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const who = res.headers.get("x-falcon-user");
  if (who && identityGuard && !url.startsWith("/api/auth/") && !url.startsWith("/api/session") && identityGuard(who)) {
    throw new ApiError(409, "IDENTITY_CHANGED", "The signed-in account changed. Reloading…");
  }
  const org = res.headers.get("x-falcon-org");
  if (org && orgGuard && !url.startsWith("/api/auth/") && !url.startsWith("/api/session") && orgGuard(org)) {
    throw new ApiError(409, "ORGANISATION_CHANGED", "You switched organisation in another tab. Opening its Home…");
  }
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const e = data?.error;
    const err = new ApiError(res.status, e?.code || "ERROR", e?.message || "Something went wrong. Please try again.", e?.field, e?.details);
    if (res.status === 429) err.retryAfter = Number(res.headers.get("retry-after")) || Number(e?.retryAfter) || undefined;
    throw err;
  }
  return data as T;
}
export const get = <T = any>(url: string) => api<T>("GET", url);
export const post = <T = any>(url: string, body: unknown = {}) => api<T>("POST", url, body);
export const patch = <T = any>(url: string, body: unknown) => api<T>("PATCH", url, body);
export const put = <T = any>(url: string, body: unknown) => api<T>("PUT", url, body);
export const del = <T = any>(url: string, body?: unknown) => api<T>("DELETE", url, body);

// ---- Formatting (dates as dd/mm/yy, as in v2.0.41) -------------------------
// ---- Days in the organisation's time zone (never the browser's or a fixed zone) ----------------------------------------
let orgTz = "";
/** Set from the session: the time zone of the organisation in use. */
export function setOrgTimeZone(tz: string | null | undefined): void { orgTz = tz || ""; }
export const orgTimeZone = (): string | undefined => orgTz || undefined;
/** Today (YYYY-MM-DD) in the organisation's time zone: the date the screens fill in for you. */
export function orgToday(): string { return dayIn(new Date(), orgTz || Intl.DateTimeFormat().resolvedOptions().timeZone); }

export function formatDate(v: unknown): string {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1].slice(-2)}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  // A moment (e.g. "issued at"): the day it fell on in the organisation's time zone.
  try { return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: orgTimeZone() }); } catch { return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
}
export function formatDateTime(v: unknown): string {
  const d = new Date(String(v ?? ""));
  if (Number.isNaN(d.getTime())) return "";
  const tz = orgTimeZone();
  try {
    return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: tz })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: tz })}`;
  } catch {
    return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
  }
}
const SYMBOL: Record<string, string> = { GBP: "£", USD: "$", EUR: "€" };
export function money(n: number, currency = "USD", dp = 2): string {
  const v = Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return SYMBOL[currency] ? `${SYMBOL[currency]}${v}` : `${currency} ${v}`;
}
export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
export function initials(name: string): string {
  return String(name || "OR").split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase() || "OR";
}

/** Coloured status pill, using the same rules as v2.0.41. */
export function badge(status: string, forced?: "green" | "amber" | "red" | "gray" | "blue"): string {
  let c = "gray";
  const s = (status || "").toLowerCase();
  if (["approved", "pass", "completed", "active", "on", "internal", "set up"].some((x) => s === x || s.includes(x))) c = "green";
  if (["pending", "trial", "awaiting", "invited", "past due", "not connected"].some((x) => s.includes(x))) c = "amber";
  if (["overdue", "suspended", "rejected", "disabled", "cancelled", "off"].some((x) => s === x || s.includes(x))) c = "red";
  return `<span class="badge ${forced || c}">${esc(status)}</span>`;
}

// ---- Modal and messages ------------------------------------------------------
export function openModal(title: string, bodyHtml: string, opts: { wide?: boolean; className?: string } = {}): HTMLElement {
  const modal = document.getElementById("modal")!;
  const card = modal.querySelector(".modal-card") as HTMLElement;
  card.className = `modal-card ${opts.wide ? "falcon-modal-wide" : "falcon-modal-narrow"} ${opts.className || ""}`;
  document.getElementById("modal-title")!.textContent = title;
  const body = document.getElementById("modal-body")!;
  body.innerHTML = bodyHtml;
  modal.classList.remove("hidden");
  document.body.classList.add("falcon-modal-open");
  const first = body.querySelector<HTMLElement>("input:not([type=hidden]):not([readonly]),select,textarea,button.primary");
  first?.focus();
  return body;
}
export function closeModal(): void {
  document.getElementById("modal")!.classList.add("hidden");
  document.getElementById("modal-body")!.innerHTML = "";
  document.body.classList.remove("falcon-modal-open");
}
export function modalOpen(): boolean {
  return !document.getElementById("modal")!.classList.contains("hidden");
}

let toastTimer: number | undefined;
export function toast(message: string, kind: "ok" | "error" = "ok"): void {
  const el = document.getElementById("toast")!;
  el.textContent = message;
  el.className = `falcon-toast ${kind}`;
  el.hidden = false;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (el.hidden = true), kind === "error" ? 7000 : 3500);
}

/** Show an error inside a form (or as a toast if the form has no message area). */
export function formError(form: HTMLElement | null, err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  const box = form?.querySelector<HTMLElement>(".form-message");
  if (box) {
    box.textContent = message;
    delete box.dataset.field;
    box.className = "form-message error";
    box.hidden = false;
    box.scrollIntoView({ block: "nearest" });
  } else {
    toast(message, "error");
  }
  // The field the message is about (from the server, or set by the screen): marked red, and the message goes as soon
  // as the field is fixed (fieldfix.ts).
  const field = err instanceof ApiError ? err.field : (err as { field?: string } | null)?.field;
  if (field && form) {
    const name = field.split(".").pop() || "";
    const input = form.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"]`);
    input?.classList.add("field-error");
    if (box && input) box.dataset.field = name;
    input?.focus();
  }
}
export function clearFormError(form: HTMLElement): void {
  const box = form.querySelector<HTMLElement>(".form-message");
  if (box) box.hidden = true;
  form.querySelectorAll(".field-error").forEach((el) => el.classList.remove("field-error"));
}

/** Read a form into a plain object. Checkboxes become true/false. */
export function formValues(form: HTMLFormElement): Record<string, any> {
  const out: Record<string, any> = {};
  for (const el of Array.from(form.elements) as HTMLInputElement[]) {
    if (!el.name || el.disabled) continue;
    if (el.type === "checkbox") out[el.name] = el.checked;
    else if (el.type === "radio") {
      if (el.checked) out[el.name] = el.value;
    } else if (el.type === "number" || el.dataset.money) out[el.name] = el.value === "" ? undefined : Number(el.value);
    else out[el.name] = el.value;
  }
  return out;
}

/** Ask a yes/no question in the v2.0.41 confirm box style. */
export function confirmBox(message: string, okLabel = "Confirm", danger = false, cancelLabel = "Cancel"): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm";
    overlay.innerHTML = `<div class="confirm-box" role="alertdialog" aria-modal="true"><h3>${esc(message)}</h3><div class="confirm-actions"><button type="button" class="secondary" data-v="no">${esc(cancelLabel)}</button><button type="button" class="${danger ? "danger" : "primary"}" data-v="yes">${esc(okLabel)}</button></div></div>`;
    document.body.appendChild(overlay);
    const done = (v: boolean) => {
      overlay.remove();
      document.removeEventListener("keydown", onKey, true);
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); done(false); }
    };
    document.addEventListener("keydown", onKey, true);
    overlay.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest("button");
      if (b) done(b.dataset.v === "yes");
      else if (e.target === overlay) done(false);
    });
    overlay.querySelector<HTMLElement>("[data-v=yes]")!.focus();
  });
}

/** Ask a question with several answers (each a button); resolves to the chosen key, or null when cancelled. A choice
 *  can carry its own button class as a third value (e.g. "secondary danger-text" for a red one). */
export function choiceBox(message: string, detail: string, choices: Array<[string, string] | [string, string, string]>): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm";
    overlay.innerHTML = `<div class="confirm-box" role="alertdialog" aria-modal="true"><h3>${esc(message)}</h3>${detail ? `<p>${esc(detail)}</p>` : ""}<div class="confirm-actions confirm-choices"><button type="button" class="secondary" data-v="">Cancel</button>${choices.map(([k, l, c], i) => `<button type="button" class="${c || (i === 0 ? "primary" : "secondary")}" data-v="${esc(k)}">${esc(l)}</button>`).join("")}</div></div>`;
    document.body.appendChild(overlay);
    const done = (v: string | null) => { overlay.remove(); document.removeEventListener("keydown", onKey, true); resolve(v); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); done(null); } };
    document.addEventListener("keydown", onKey, true);
    overlay.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest("button");
      if (b) done(b.dataset.v || null);
      else if (e.target === overlay) done(null);
    });
    overlay.querySelector<HTMLElement>("button.primary")?.focus();
  });
}

/** Ask for a short text (a reason or a change note) in the confirm box style. Resolves to the text, or null if cancelled. */
export function promptBox(message: string, label: string, okLabel = "Save", required = true): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm";
    overlay.innerHTML = `<form class="confirm-box falcon-prompt" role="alertdialog" aria-modal="true" novalidate><h3>${esc(message)}</h3><label class="falcon-prompt-label"><span>${esc(label)}</span><textarea rows="3" maxlength="500"></textarea></label><p class="form-message error" hidden>Please fill this in.</p><div class="confirm-actions"><button type="button" class="secondary" data-v="no">Cancel</button><button type="submit" class="primary">${esc(okLabel)}</button></div></form>`;
    document.body.appendChild(overlay);
    const box = overlay.querySelector<HTMLFormElement>("form")!, text = box.querySelector("textarea")!;
    const done = (v: string | null) => { overlay.remove(); document.removeEventListener("keydown", onKey, true); resolve(v); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); done(null); } };
    document.addEventListener("keydown", onKey, true);
    box.addEventListener("submit", (e) => {
      e.preventDefault();
      const v = text.value.trim();
      if (required && !v) { box.querySelector<HTMLElement>(".form-message")!.hidden = false; text.focus(); return; }
      done(v);
    });
    overlay.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("[data-v=no]") || e.target === overlay) done(null); });
    text.focus();
  });
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Could not read that file."));
    r.readAsDataURL(file);
  });
}

export async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast("Copied.");
  } catch {
    toast("Couldn't copy automatically. Select the text and copy it.", "error");
  }
}
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Could not read that file."));
    r.readAsText(file);
  });
}
